import {
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
  signal,
} from '@angular/core';
import { PhotoContestApiService } from 'apps/account/src/account-shared/services/photo-contest-api.service';
import { TranslateService } from '@ngx-translate/core';
import { UrlFormatter } from 'shared';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { buildAzureStoredPhotoUrl } from 'apps/account/src/account-shared/pipes/azure-stored-photo.util';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { CurationDetailsNavigatorService } from './curation-details-navigator.service';
import { FileSaverService } from 'apps/account/src/account-shared/services/fileSaver.service';
import { ShareOAuthService } from '../../../../features/stories/broadcast/share-oauth.service';
import { FtModalChromeComponent } from '../../modal/ft-modal-chrome.component';
import { FtBulkCurationNavComponent } from './ft-bulk-curation-nav.component';
import { FtCurationModalComponent } from './ft-curation-modal.component';
import { FtCurationImageDetailsComponent } from './ft-curation-image-details.component';
import { FtCurationImageReplacementComponent } from './ft-curation-image-replacement.component';
import { FtCurationShareComponent } from './ft-curation-share.component';
import { FtShareImageCreatedComponent } from './ft-share-image-created.component';
import { FtCurationCreateProgressComponent } from './ft-curation-create-progress.component';
import { NativeCurationModalService } from './native-curation-modal.service';
import { BroadcastModalService } from '../../../../features/stories/broadcast/broadcast-modal.service';

/**
 * Host for the native curation modal: orchestrates the curation action handlers and the
 * cross-modal flow (curation → image-details / replacement / share → progress →
 * shareImageCreated) via FtModalChromeComponent. Mounted onto document.body by
 * NativeCurationModalService via createComponent; reads the shared service.model.
 * PhotoContest mutators resolve inside NgZone.run; events fan out through the service.
 */
@Component({
    selector: 'ft-curation-modal-host-ng',
    imports: [
        FtModalChromeComponent,
        FtBulkCurationNavComponent,
        FtCurationModalComponent,
        FtCurationImageDetailsComponent,
        FtCurationImageReplacementComponent,
        FtCurationShareComponent,
        FtShareImageCreatedComponent,
        FtCurationCreateProgressComponent,
    ],
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./curation-modal-host.component.scss'],
    templateUrl: './curation-modal-host.component.html'
})
export class CurationModalHostComponent implements OnInit, OnDestroy {
    private readonly service = inject(NativeCurationModalService);
    private readonly zone = inject(NgZone);
    private readonly detailsNavigator = inject(CurationDetailsNavigatorService);

    // Cross-modal visibility (mutually exclusive — only one panel at a time, as v1's
    // ftModal.hide(a).then(show(b))).
    protected readonly curationOpen = signal(true);
    protected readonly detailsOpen = signal(false);
    protected readonly replacementOpen = signal(false);
    protected readonly shareOpen = signal(false);
    protected readonly shareCreatedOpen = signal(false);
    protected readonly progressOpen = signal(false);

    /** v1 `scope.sharing` (built on openShareModal — the dead share chain). */
    protected readonly sharing = signal<any>(null);
    protected readonly quoteSize = signal<number | undefined>(undefined);
    protected readonly captionSize = signal<number | undefined>(undefined);
    protected readonly shareMessageLimit = signal<number | undefined>(undefined);

    /** ft-snapshot of guest.poststay.photo, taken when an image panel opens. */
    private photoSnapshot: any;
    /** Bulk-progress transition tracking (v1 bulkHeaderScope.isDone). */
    private bulkWasDone = false;

    // --- native services (fully drained off the $injector bridge, inc 2q) ---
    private readonly photoContest: any;
    private readonly ftAzureStoredPhoto: (photo: any, size?: string) => string;
    private readonly translate: (key: string, params?: any) => string;
    private readonly parse: any;
    // WS5 #10 (BroadcastModal port): the $rootScope grab died with the run-block
    // bridge — broadcastStory() calls the native opener directly.
    private readonly broadcastModal = inject(BroadcastModalService);
    private readonly shareOAuth = inject(ShareOAuthService);
    private readonly fileSaver = inject(FileSaverService);
    /** v1 grabbed `$window` — a thin wrapper over the global. */
    private readonly window: any = window;

    private readonly messageLimitPerNetwork = { fb: 850, li: 850, tw: 116 };

    private readonly onCurationProgress = () => this.handleCurationProgress();

    constructor() {
        this.photoContest = inject(PhotoContestApiService);
        const environment = inject(ENVIRONMENT_TOKEN) as Environment;
        this.ftAzureStoredPhoto = (photo: any, size?: string) =>
            buildAzureStoredPhotoUrl(photo, size, environment.AzureCdnBaseUrl) as string;
        const translateSvc = inject(TranslateService);
        this.translate = (key: string, params?: any) => translateSvc.instant(key, params);
        this.parse = inject(ParseService);
    }

    ngOnInit(): void {
        if (this.isBulk) {
            this.bulkWasDone = this.isCurated();
            this.service.on('photo-rated', this.onCurationProgress);
            this.service.on('caption-rated', this.onCurationProgress);
            this.service.on('quote-rated', this.onCurationProgress);
            this.service.on('pass', this.onCurationProgress);
            this.service.on('finalist', this.onCurationProgress);
        }
        // v1 directive set $window.fliptoCurationShareComplete (the OAuth share callback).
        this.window.fliptoCurationShareComplete = (state: any) => {
            this.shareOAuth.log('curation-share', { action: 'complete', state });
            this.zone.run(() => {
                if (state == 0 || state == false || typeof state === 'string') {
                    this.window.alert(this.translate('flipto.account.CurationShareError'));
                } else if (state == 1 || state == true) {
                    this.sharing().network.totalShareTimes = this.parse.intOrDefault(this.sharing().network.totalShareTimes) + 1;
                }
                this.shareOAuth.close();
                this.shareCreatedOpen.set(false);
            });
        };
    }

    ngOnDestroy(): void {
        if (this.isBulk) {
            this.service.off('photo-rated', this.onCurationProgress);
            this.service.off('caption-rated', this.onCurationProgress);
            this.service.off('quote-rated', this.onCurationProgress);
            this.service.off('pass', this.onCurationProgress);
            this.service.off('finalist', this.onCurationProgress);
        }
    }

    /** Called by the service after moveNext swaps the bound guest. */
    onGuestChanged(): void {
        this.bulkWasDone = this.isCurated();
    }

    /** v1 `scope.close` — the #curation X tears the modal down (+ onClose callback). */
    protected closeModal(): void {
        this.service.close();
    }

    // ---- shared model accessors ----

    protected get model(): any {
        return this.service.model;
    }
    protected get guest(): any {
        return this.model?.guest;
    }
    protected get property(): any {
        return this.model?.property;
    }
    private get isBulk(): boolean {
        return !!this.model?.isBulk;
    }
    protected get uploadUrl(): string | undefined {
        const photo = this.guest?.poststay?.photo;
        if (!photo) return undefined;
        return UrlFormatter.format(
            '/api/:propertyUuid/properties/layers/photo-advocacy/contests/photos/:photoId',
            { photoId: photo.uuid, propertyUuid: this.property.UUID },
        );
    }

    /** Bulk header state for the #curation panel header + chrome footer. */
    protected get bulkNav(): { hasPhoto: boolean; isDone: boolean; total: number } | null {
        if (!this.isBulk) return null;
        return { hasPhoto: this.hasPhoto(), isDone: this.isCurated(), total: this.model.bulkTotal };
    }

    // ---- bulk paging + progress ----

    protected moveNext(): void {
        this.service.moveNext();
        const header = document.querySelector('#curation .modal-header');
        if (header && (header as any).scrollIntoView) {
            (header as any).scrollIntoView({ behavior: 'smooth' });
        }
    }

    private handleCurationProgress(): void {
        const isDone = this.isCurated();
        if (!this.bulkWasDone && isDone) {
            this.service.emit('curated', this.guest.uuid);
        }
        this.bulkWasDone = isDone;
    }

    // ---- 11 action handlers (verbatim from curationModal.directive.js) ----

    protected ratePhoto(rating: number): void {
        const photo = this.guest.poststay.photo;
        photo.internalInfo = photo.internalInfo || {};
        photo.internalInfo['rating'] = rating;
        this.photoContest.ratePhoto(this.property.UUID, photo.uuid, rating);
        this.service.emit('photo-rated', [photo.uuid, rating]);
        // WS5 #10 inc 2i: the extra $rootScope.$broadcast('curationModal.photo-rated') is
        // REMOVED — its only listener (the ft2 story-card) was a NO-OP ($on signature bug:
        // the handler bound the event object as `args`) and is deleted with it.
    }

    protected rateCaption(rating: number): void {
        const photo = this.guest.poststay.photo;
        photo.internalInfo = photo.internalInfo || {};
        photo.internalInfo['captionRating'] = rating;
        this.photoContest.rateCaption(this.property.UUID, photo.uuid, rating);
        this.service.emit('caption-rated', [photo.uuid, rating]);
        // WS5 #10 inc 2i: the extra 'curationModal.caption-rated' $broadcast is REMOVED
        // (same no-op story-card listener as photo-rated — deleted together).
    }

    protected rateQuote(rating: number): void {
        const quote = this.guest.poststay.quote;
        quote.internalInfo = quote.internalInfo || {};
        quote.internalInfo['rating'] = rating;
        this.photoContest.rateQuote(this.property.UUID, this.guest.uuid, rating);
        this.service.emit('quote-rated', [this.guest.uuid, rating]);
    }

    protected pass(): void {
        const photo = this.guest.poststay.photo;
        Promise.resolve(this.photoContest.pass(this.property.UUID, photo.uuid)).then(() => {
            this.zone.run(() => {
                photo.isFinalist = false;
                photo.isPassed = true;
                this.service.emit('pass', photo.uuid);
            });
        });
    }

    protected advanceToFinal(): void {
        if (!this.ensureFinalistEmailIsActive()) return;
        const photo = this.guest.poststay.photo;
        Promise.resolve(this.photoContest.advanceToFinal(this.property.UUID, photo.uuid)).then(() => {
            this.zone.run(() => {
                photo.isPassed = false;
                photo.isFinalist = true;
                this.service.emit('finalist', photo.uuid);
            });
        });
    }

    protected showImageModal(): void {
        this.photoSnapshot = this.copy(this.guest.poststay.photo);
        this.curationOpen.set(false);
        this.detailsOpen.set(true);
    }

    protected showReplaceModal(): void {
        this.photoSnapshot = this.copy(this.guest.poststay.photo);
        this.curationOpen.set(false);
        this.replacementOpen.set(true);
    }

    protected showCurationModal(): void {
        this.detailsOpen.set(false);
        this.replacementOpen.set(false);
        this.shareOpen.set(false);
        this.shareCreatedOpen.set(false);
        this.progressOpen.set(false);
        this.curationOpen.set(true);
    }

    /** Image panel Cancel → ft-snapshot rollback of guest.poststay.photo + back to curation. */
    protected cancelImagePanel(_panel: 'details' | 'replacement'): void {
        if (this.photoSnapshot) {
            this.guest.poststay.photo = this.copy(this.photoSnapshot);
            this.photoSnapshot = undefined;
        }
        this.showCurationModal();
    }

    protected replacePhoto(): void {
        const photo = this.guest.poststay.photo;
        photo.isUpdated = true;
        this.service.emit('photo-updated', photo.uuid);
        this.photoSnapshot = undefined; // committed
        this.replacementOpen.set(false);
        this.curationOpen.set(true);
    }

    protected savePhotoDetails(): void {
        const photo = this.guest.poststay.photo;
        const model: any = {
            caption: photo.caption,
            rotation: this.parseDegree(photo.degrees),
        };
        if (this.guest.poststay.quote) {
            model.quote = this.guest.poststay.quote.notesPositive;
        }
        Promise.resolve(this.photoContest.setPhotoInfo(this.property.UUID, photo.uuid, model))
            .then(() => {
                this.zone.run(() => {
                    if (model.rotation > 0) {
                        photo.isUpdated = true;
                        photo.photoUrl = this.ftAzureStoredPhoto(photo, 'small') + `?timestamp=${new Date().getTime()}`;
                        photo.degrees = 0;
                    }
                    this.service.emit('photo-updated', photo.uuid);
                });
            })
            .finally(() => {
                this.zone.run(() => {
                    this.photoSnapshot = undefined; // committed
                    this.detailsOpen.set(false);
                    this.curationOpen.set(true);
                });
            });
    }

    protected reprocessImage(): void {
        const photo = this.guest.poststay.photo;
        Promise.resolve(this.photoContest.reprocessPhoto(this.property.UUID, photo.uuid)).then(() => {
            this.zone.run(() => {
                photo.isUpdated = true;
                this.service.emit('photo-updated', photo.uuid);
            });
        });
    }

    protected downloadHighResImage(): void {
        Promise.resolve(this.photoContest.downloadPhoto(this.property.UUID, this.guest.poststay.photo.uuid)).then((data: any) => {
            this.fileSaver.saveBlob(data.content, data.fileName);
        });
    }

    protected openAdvocatePage(): void {
        // WS5 #10 inc 2i-2: the details-page navigation runs natively now (the run-block
        // 'curationModal.openDetails' handler moved to CurationDetailsNavigatorService).
        this.detailsNavigator.openDetails(this.guest.uuid);
    }

    protected broadcastStory(): void {
        // WS5 #10 (BroadcastModal port): was a $rootScope '[StoryModal].BroadcastStory'
        // broadcast into the angularjs.module.ts run-block bridge (the LAST bridge,
        // now deleted) — direct native call, same args.
        this.broadcastModal.broadcastStoryFromStoryModal(this.property.UUID, this.guest.uuid);
    }

    // ---- share chain (DEAD: no in-app caller — kept faithful for completeness) ----

    openShareModal(network: any): void {
        this.quoteSize.set(this.model.maxContentSize);
        this.captionSize.set(this.model.maxContentSize);
        this.shareMessageLimit.set(this.networkMessageLimit(network));
        this.sharing.set({
            network,
            backgroundIsGuestPhoto: !!this.guest.poststay.photo,
            isShowProfilePic: !!this.guest.profileImageUrl,
            isShowGuestName: this.guest.first && this.guest.first.length > 0,
            isShowGuestQuote: false,
            isShowPhotoCaption: false,
            isLandscape: this.isLandscape(),
            isRtl: this.isRtl(),
        });
        this.curationOpen.set(false);
        this.shareOpen.set(true);
    }

    protected createShareImage(): void {
        const sharing = this.sharing();
        const network = sharing.network;
        const guest = this.guest;
        const property = this.property;
        this.shareOpen.set(false);
        this.curationOpen.set(false);
        this.progressOpen.set(true);
        const model: any = {
            GuestUUID: guest.uuid,
            SocialApplication: this.parse.int(network.socialNetworkType),
            PhotoUUID: sharing.backgroundIsGuestPhoto ? guest.poststay.photo.uuid : null,
            isShowPhotoCaption: !!(sharing.backgroundIsGuestPhoto && sharing.isShowPhotoCaption),
            isShowGuestPhoto: sharing.backgroundIsGuestPhoto,
            SelectedPhotoUrl: sharing.backgroundIsGuestPhoto
                ? this.ftAzureStoredPhoto(guest.poststay.photo)
                : property.BackgroundImageUrl,
            isShowGuestName: !!sharing.isShowGuestName,
            isShowProfilePic: !!sharing.isShowProfilePic,
            isShowGuestQuote: !!sharing.isShowGuestQuote,
        };
        Promise.resolve(this.photoContest.createCurationShareImage(property.UUID, model)).then(
            (shareImageUrl: string) => {
                this.zone.run(() => {
                    this.progressOpen.set(false);
                    sharing.shareImageUrl = shareImageUrl;
                    this.shareCreatedOpen.set(true);
                });
            },
            () => {
                this.zone.run(() => {
                    this.progressOpen.set(false);
                    this.shareOpen.set(true);
                    this.window.alert(this.translate('flipto.account.CurationShareError'));
                });
            },
        );
    }

    protected downloadShareImage(isStayOpen: boolean): void {
        const sharing = this.sharing();
        Promise.resolve(this.photoContest.downloadShareImage(this.property.UUID, sharing.shareImageUrl)).then(
            (data: any) => {
                this.fileSaver.saveBlob(data.content, data.fileName);
                if (!isStayOpen) {
                    this.zone.run(() => {
                        sharing.network.totalShareTimes = this.parse.intOrDefault(sharing.network.totalShareTimes) + 1;
                        this.shareCreatedOpen.set(false);
                    });
                }
            },
        );
    }

    protected openOAuth(): void {
        const sharing = this.sharing();
        const network = sharing.network;
        const guest = this.guest;
        const model: any = {
            companyCode: this.model.companyCode,
            languageCode: 'en',
            socialApp: network.socialNetworkType,
            shareMessage: sharing.message,
            shareImageUUID: guest.poststay.photo ? guest.poststay.photo.uuid : null,
            photoUUID: guest.poststay.photo ? guest.poststay.photo.uuid : null,
            shareImageUrl: sharing.shareImageUrl,
            isLandscape: sharing.isLandscape,
            callback: 'fliptoCurationShareComplete',
        };
        this.shareOAuth.log('curation-share', { action: 'open', state: model });
        this.shareOAuth.tryOpenWindow(network.socialNetworkType);
        this.shareOAuth.trySetLocation(network.socialNetworkType, model);
    }

    // ---- pure helpers (verbatim) ----

    private hasPhoto(): boolean {
        const guest = this.guest;
        return guest && guest.poststay && guest.poststay.photo !== undefined;
    }

    private hasCaption(): boolean {
        const guest = this.guest;
        return (
            guest && guest.poststay && guest.poststay.photo && guest.poststay.photo.caption && guest.poststay.photo.caption.length > 0
        );
    }

    private hasQuote(): boolean {
        const guest = this.guest;
        return guest && guest.poststay && guest.poststay.quote && guest.poststay.quote.notesPositive !== undefined;
    }

    private isLandscape(): boolean {
        const guest = this.guest;
        return (
            guest &&
            guest.poststay &&
            guest.poststay.photo &&
            this.parse.intOrDefault(guest.poststay.photo.photoWidth) > this.parse.intOrDefault(guest.poststay.photo.photoHeight)
        );
    }

    private isRtl(): boolean {
        const guest = this.guest;
        return guest && guest.isRtl && !!+guest.isRtl;
    }

    private networkMessageLimit(network: any): number | undefined {
        if (!network) return undefined;
        if (network.socialNetworkType == 3) return this.messageLimitPerNetwork.li;
        if (network.socialNetworkType == 1) return this.messageLimitPerNetwork.fb;
        if (network.socialNetworkType == 2) return this.messageLimitPerNetwork.tw;
        return undefined;
    }

    private isCurated(): boolean {
        const guest = this.guest;
        if (this.hasPhoto()) {
            const photo = guest.poststay.photo;
            let isCurated = this.isNotReviewed(photo) || photo.isPassed || photo.isFinalist;
            isCurated = isCurated && photo.internalInfo !== undefined && photo.internalInfo['rating'] !== undefined;
            if (this.hasCaption()) {
                isCurated = isCurated && photo.internalInfo !== undefined && photo.internalInfo['captionRating'] !== undefined;
            }
            if (this.hasQuote() && this.parse.bool(guest.poststay.quote.isPositive)) {
                isCurated = isCurated && guest.poststay.quote.internalInfo !== undefined && guest.poststay.quote.internalInfo['rating'] !== undefined;
            }
            return isCurated;
        } else if (this.hasQuote()) {
            return (
                this.parse.bool(guest.poststay.quote.isPositive) &&
                guest.poststay.quote.internalInfo !== undefined &&
                guest.poststay.quote.internalInfo['rating'] !== undefined
            );
        }
        return false;
    }

    private isNotReviewed(photo: any): boolean {
        return photo.photoContest.isClosed && !(photo.isPassed || photo.isFinalist);
    }

    private ensureFinalistEmailIsActive(): boolean {
        if (!this.model.isFinalistEmailActive) {
            alert('Finalist email is disabled, please enable it before curating guests.');
            return false;
        }
        return true;
    }

    private parseDegree(degrees: number): number {
        const angle = degrees - 360 * Math.floor(degrees / 360);
        return Math.floor(angle / 90);
    }

    private copy<T>(value: T): T {
        return value == null ? value : (JSON.parse(JSON.stringify(value)) as T);
    }
}
